import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './style.scss';
import { DefaultSwitch as Switch } from 'services/UIUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import moment from 'moment-timezone';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import DefaultTextField from 'components/Utils/DefaultTextField';
import StaffTimePicker from 'components/Utils/StaffTimePicker';
import MenuItem from '@material-ui/core/MenuItem';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import StackItemsView from 'components/Common/StackItemsView';
import IconButton from '@material-ui/core/IconButton';
import Icon from 'components/Utils/NewIcon';
import CalendarUtils from 'services/CalendarUtils';
import ShiftUtils from 'services/ShiftUtils';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import ClinicSelector from 'components/Utils/ClinicSelector';
import ConfirmationButton from '../../../Utils/NewDefaultButton/ConfirmationButton';
import MiscUtils from 'services/MiscUtils';

export const NON_WORKING_REASONS = [
  { value: 'Vacation', label: 'Paid Time Off' },
  { value: 'STAT', label: 'STAT' },
  { value: 'Sick', label: 'Sick' },
  { value: 'Bereavement', label: 'Bereavement' },
  { value: 'Education', label: 'Education' },
  { value: 'Emergency', label: 'Emergency' },
];

const EditShift = ({ shift, onSave, onClose }) => (
  <div className="shift-editor full-height">
    <div className="details-panel shift-details padded-box full-width">
      <ShiftDetails shift={shift} onSave={onSave} onClose={onClose} />
    </div>
  </div>
);

EditShift.propTypes = {
  shift: PropTypes.object,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
};

const CANCEL_INPUT = 'Select reason for shift cancellation';

const ShiftDetails = ({ shift = {}, onSave, onClose }) => {
  const dispatch = useAppDispatch();
  const [data, setData] = useState({ ...shift });
  const [cancelling, setCancelling] = useState(false);
  const [cancellingAll, setCancellingAll] = useState(false);
  const [moving, setMoving] = useState(false);
  const [breakValue, setBreakValue] = useState({});
  const isLoading = useIsLoading(api.Shifts.addOrEditShifts);

  const isPast = moment.unix(shift?.start).isBefore(moment().startOf('day'));

  function onChangeTime(field, time) {
    let date = moment.unix(shift.start);
    time = moment.unix(time);
    date.hour(time.hour());
    date.minute(time.minute());
    setData({ ...data, [field]: date.unix() });
  }

  function onSaveWrapper() {
    if (breakValue.name && breakValue.start_hour && breakValue.end_hour) {
      data.breaks = [...(data.breaks || []), breakValue];
    }

    if (data.start > data.end) {
      data.end = moment.unix(data.end).add(24, 'hours').unix();
    }
    if (data.non_working) delete data.breaks;
    setBreakValue({});
    onSave(data);
  }

  // moveAppointments false = "Cancel Shift & Cancel All Appointments": the
  // rescue pass is skipped entirely and every appointment on the shift is
  // cancelled outright (patients notified, fees refunded).
  async function onCancelWrapper(input, moveAppointments = true) {
    const setLoading = moveAppointments ? setCancelling : setCancellingAll;
    setLoading(true);
    const result = await dispatch(
      api.Shifts.cancelShift({
        shiftId: shift.id,
        reason: input,
        moveAppointments,
        shiftCancelRequest: {},
      }),
      { errorTitle: 'Cancel Shift' },
    );
    if (result) {
      onClose();
    }
    setLoading(false);
  }

  async function onMoveWrapper() {
    setMoving(true);
    const result = await dispatch(api.Shifts.moveShiftAppointments({ shiftId: shift.id }), {
      errorTitle: 'Move Appointments',
    });
    if (result) {
      MiscUtils.showSuccess('Move Appointments', result.value);
      onClose();
    }
    setMoving(false);
  }

  function deleteBreak(data) {
    setData((prevState) => {
      return {
        ...prevState,
        breaks: prevState.breaks.filter((b) => b !== data),
      };
    });
  }

  function onChangeBreakTime(name, val) {
    let militaryTime = CalendarUtils.format(val, 'HHmm');
    setBreakValue((prevState) => {
      return { ...prevState, [name]: militaryTime };
    });
  }

  function addBreak() {
    if (breakValue.start_hour && breakValue.end_hour && breakValue.name) {
      setData((prevState) => {
        return {
          ...prevState,
          breaks: [...(prevState.breaks || []), breakValue],
        };
      });
      setBreakValue({});
    }
  }

  return (
    <div className="shift-details full-width">
      <div className="row">
        <DefaultDatePicker
          label="Date"
          name="date"
          value={shift.start}
          disabled={true}
          style={{ width: '150px' }}
        />
        <StaffTimePicker
          value={data.start}
          label="Start Hour"
          name="start"
          onChange={(event, val) => onChangeTime('start', val)}
          style={{ width: '150px' }}
        />
        <StaffTimePicker
          value={data.end}
          label="End Hour"
          name="end"
          onChange={(event, val) => onChangeTime('end', val)}
          style={{ width: '150px' }}
        />
      </div>
      <div className="row mt-2">
        <ClinicSelector
          setValue={(id) => setData({ ...data, clinic_id: id })}
          value={data.clinic_id}
        />
      </div>
      <div className="row mt-2">
        <div className="wrapper">
          <Switch
            label="Non-working"
            padded={false}
            checked={!!data.non_working}
            onChange={(event, isInputChecked) => {
              setData({
                ...data,
                non_working: isInputChecked,
                non_working_reason: isInputChecked ? NON_WORKING_REASONS[0].value : undefined,
                half_day: undefined,
              });
            }}
          />
        </div>
        <div className="wrapper">
          <Switch
            label="Approved"
            padded={false}
            checked={data.approved === undefined || data.approved === true}
            onChange={(event, isInputChecked) => {
              setData({
                ...data,
                approved: isInputChecked,
              });
            }}
          />
        </div>
      </div>
      {!data.non_working && (
        <>
          <div className="wrapper">
            <Switch
              label="Telemedicine Only"
              padded={false}
              checked={!!data.telemedicine_only}
              onChange={(event, isInputChecked) => {
                setData({
                  ...data,
                  telemedicine_only: isInputChecked,
                });
              }}
            />
          </div>
          <span className="small-title mt-2">Breaks</span>
          <StackItemsView
            stack={data.breaks}
            onDeleteItem={deleteBreak}
            describeItem={ShiftUtils.getBreakDescription}
            hideStatus={true}
          />
          <div className="row breaks-input">
            <DefaultSelectField
              label="Break Name"
              width="250px"
              value={breakValue.name}
              onChange={(event) =>
                setBreakValue((prevState) => {
                  return { ...prevState, name: event.target.value };
                })
              }
              inputs={Object.values(ShiftUtils.getBreakReasons())}
              style={{ width: '120px' }}
            />
            <StaffTimePicker
              value={breakValue.start}
              label="Break Start Hour"
              name="start_hour"
              onChange={(event, val) => onChangeBreakTime('start_hour', val)}
              style={{ width: '150px' }}
            />
            <StaffTimePicker
              value={breakValue.end}
              label="Break End Hour"
              name="end_hour"
              onChange={(event, val) => onChangeBreakTime('end_hour', val)}
              style={{ width: '150px' }}
            />
            <IconButton
              onClick={addBreak}
              className="blue"
              size="small"
              style={{ width: '30px', height: '30px' }}
            >
              <Icon src="plus" className="white" showTooltip={true} alt="Add Break" />
            </IconButton>
          </div>
        </>
      )}
      {!!data.non_working && (
        <>
          <DefaultSelectField
            name="non_working_reason"
            label="Non-working Reason"
            value={data.non_working_reason}
            onChange={(event) =>
              setData({
                ...data,
                non_working_reason: event.target.value,
                half_day: undefined,
              })
            }
            inputElements={NON_WORKING_REASONS.map((option) => (
              <MenuItem
                id={'menu-item-' + (option.value || 'None')}
                value={option.value}
                key={option.value || 'None'}
              >
                {option.label || 'None'}
              </MenuItem>
            ))}
            style={{ width: '100%' }}
          />
          {data.non_working_reason === NON_WORKING_REASONS[0].value && (
            <DefaultSelectField
              name="half_day"
              label="Half Day"
              value={data.half_day}
              onChange={(event) => setData({ ...data, half_day: event.target.value })}
              inputElements={[
                { value: undefined, label: 'Full day off' },
                { value: '1', label: '1st half off' },
                { value: '2', label: '2nd half off' },
              ].map((option) => (
                <MenuItem
                  id={'menu-item-' + (option.value || 'None')}
                  value={option.value}
                  key={option.value || 'None'}
                >
                  {option.label || 'None'}
                </MenuItem>
              ))}
              style={{ width: '100%' }}
            />
          )}
          <DefaultTextField
            label="Notes"
            style={{ width: '100%' }}
            name="notes"
            value={data.notes}
            onChange={(event) => setData({ ...data, notes: event.target.value })}
          />
        </>
      )}
      <DefaultButton
        name={shift.end ? 'Save Shift' : 'Create Custom Shift'}
        style={{ marginTop: '25px', width: '350px' }}
        className="blue"
        onClick={() => onSaveWrapper(data)}
        loading={isLoading}
      />
      {!!shift.id && !isPast && (
        <>
          <ConfirmationButton
            name="Move Appointments"
            style={{ marginTop: '15px', width: '350px' }}
            className="blue"
            onClick={() => onMoveWrapper()}
            confirmation={{
              message:
                'This moves appointments to other available practitioners (same time, or up to 15 minutes later) WITHOUT cancelling the shift. Appointments that cannot be moved are left unchanged. Affected patients are notified by SMS/email that their appointment changed.',
              confirmLabel: 'Yes, move appointments',
              cancelLabel: 'No, leave it as it is',
            }}
            loading={moving}
          />
          <ConfirmationButton
            name="Cancel Shift & Move/Cancel Appointments"
            style={{ marginTop: '15px', width: '350px' }}
            className="red"
            onClick={(e, form) => onCancelWrapper(form[CANCEL_INPUT])}
            confirmation={{
              message:
                'This will move any appointments to other practitioners if a slot is available at the same time (or up to 15 minutes later) or cancel them otherwise. Affected patients will be notified via SMS.',
              confirmLabel: 'Yes, cancel shift',
              cancelLabel: 'No, leave it as it is',
              form: [
                {
                  type: 'autocomplete',
                  label: CANCEL_INPUT,
                  inputs: NON_WORKING_REASONS.map((it) => it.value),
                },
              ],
            }}
            loading={cancelling}
          />
          {/* Deliberately NOT a second solid-red pill. This sits next to a
              button whose label differs only in the middle, and the two do
              semantically opposite things with the patients — one rescues them
              onto a colleague, this one cancels them outright. Outline-danger
              plus the extra top margin makes the rarer, more drastic action
              visually separate rather than a same-looking neighbour one row
              down. */}
          <ConfirmationButton
            name="Cancel Shift & Cancel All (No Move)"
            style={{
              marginTop: '25px',
              width: '350px',
              color: 'var(--color-status-danger)',
              border: '1px solid var(--color-status-danger)',
            }}
            className="white"
            onClick={(e, form) => onCancelWrapper(form[CANCEL_INPUT], false)}
            confirmation={{
              message:
                'This cancels the shift and cancels EVERY appointment on it outright — no attempt is made to move patients to another practitioner. Affected patients will be notified via SMS and any fees are refunded.',
              confirmLabel: 'Yes, cancel all appointments',
              cancelLabel: 'No, leave it as it is',
              form: [
                {
                  type: 'autocomplete',
                  label: CANCEL_INPUT,
                  inputs: NON_WORKING_REASONS.map((it) => it.value),
                },
              ],
            }}
            loading={cancellingAll}
          />
        </>
      )}
    </div>
  );
};

export default EditShift;
